import { useState, useEffect } from 'react';
import { Plus, Cpu, Trash2, Edit2, Loader2 } from 'lucide-react';
import { getSkills, createSkill, updateSkill, deleteSkill } from '../../services/skillsApi';
import type { Skill } from '../../services/skillsApi';
import * as LucideIcons from 'lucide-react';

export function AdminSkills() {
    const [skills, setSkills] = useState<Skill[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const [formData, setFormData] = useState({
        name: '',
        category: 'languages',
        level: '',
        icon_name: ''
    });

    useEffect(() => {
        fetchSkills();
    }, []);

    const fetchSkills = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getSkills();
            setSkills(data || []);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur réseau');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenForm = (skill?: Skill) => {
        if (skill) {
            setShowForm(true);
            setEditingId(skill.id);
            setFormData({
                name: skill.name,
                category: skill.category,
                level: skill.level ? skill.level.toString() : '',
                icon_name: skill.icon_name || ''
            });
        } else {
            setShowForm(true);
            setEditingId(null);
            setFormData({
                name: '',
                category: 'languages',
                level: '',
                icon_name: ''
            });
        }
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingId(null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            setError(null);

            const expData = {
                name: formData.name,
                category: formData.category,
                level: formData.level ? parseInt(formData.level, 10) : undefined,
                icon_name: formData.icon_name || undefined
            };

            if (editingId) {
                await updateSkill(editingId, expData);
            } else {
                await createSkill(expData);
            }

            await fetchSkills();
            setShowForm(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Une erreur est survenue');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Voulez-vous vraiment supprimer cette compétence ?')) return;

        try {
            setLoading(true);
            await deleteSkill(id);
            await fetchSkills();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Erreur lors de la suppression');
            setLoading(false);
        }
    };

    if (loading && skills.length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-[#1f75cb]" />
            </div>
        );
    }

    return (
        <div className="space-y-6 flex flex-col items-center max-w-5xl mx-auto">
            <div className="flex justify-between items-center w-full">
                <h1 className="text-2xl font-bold text-[#1d1d1f] flex items-center gap-2">
                    <Cpu className="w-8 h-8 text-[#fc6d26]" />
                    Compétences
                </h1>
                <button
                    onClick={() => handleOpenForm()}
                    className="flex items-center gap-2 px-4 py-2 bg-[#1f75cb] text-white rounded-lg hover:bg-[#1a62a8] transition-colors"
                >
                    <Plus className="w-4 h-4" />
                    Ajouter
                </button>
            </div>

            {error && (
                <div className="bg-red-50 text-red-600 p-4 rounded-lg border border-red-100 w-full">
                    {error}
                </div>
            )}

            {showForm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <h2 className="text-xl font-bold mb-6">
                            {editingId ? 'Modifier la compétence' : 'Nouvelle compétence'}
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                                <input
                                    type="text"
                                    required
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20"
                                    placeholder="Ex: Python"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                                <select
                                    required
                                    value={formData.category}
                                    onChange={e => setFormData({ ...formData, category: e.target.value })}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20"
                                >
                                    <option value="languages">Langages</option>
                                    <option value="infrastructure">Infrastructure</option>
                                    <option value="cloud">Cloud</option>
                                    <option value="soft">Soft Skills</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Niveau (%) - (Optionnel)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={formData.level}
                                    onChange={e => setFormData({ ...formData, level: e.target.value })}
                                    className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20"
                                    placeholder="80"
                                />
                            </div>

                            {formData.category === 'soft' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom d'icône Lucide (Optionnel)</label>
                                    <input
                                        type="text"
                                        value={formData.icon_name}
                                        onChange={e => setFormData({ ...formData, icon_name: e.target.value })}
                                        className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#1f75cb]/20"
                                        placeholder="Target, Users, Lightbulb..."
                                    />
                                    <p className="text-xs text-gray-400 mt-1">Référencez les icônes sur lucide.dev ("Target", "Users", etc.)</p>
                                </div>
                            )}

                            <div className="flex justify-end gap-3 mt-8">
                                <button
                                    type="button"
                                    onClick={handleCloseForm}
                                    className="px-4 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
                                >
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="px-4 py-2 bg-[#1f75cb] text-white rounded-lg hover:bg-[#1a62a8] transition-colors flex items-center justify-center font-medium disabled:opacity-50"
                                >
                                    {isSubmitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enregistrer'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {skills.length === 0 && !loading && !error ? (
                <div className="text-center py-12 bg-white rounded-xl border border-gray-100 w-full">
                    <Cpu className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Aucune compétence n'est configurée pour le moment.</p>
                </div>
            ) : (
                <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {skills.map(skill => {
                        const IconComponent = skill.icon_name ? (LucideIcons as any)[skill.icon_name] : null;
                        return (
                            <div key={skill.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 relative group">
                                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <button onClick={() => handleOpenForm(skill)} className="p-1 bg-blue-50 text-blue-600 rounded">
                                        <Edit2 className="w-3 h-3" />
                                    </button>
                                    <button onClick={() => handleDelete(skill.id)} className="p-1 bg-red-50 text-red-600 rounded">
                                        <Trash2 className="w-3 h-3" />
                                    </button>
                                </div>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-xs font-semibold px-2 py-0.5 rounded bg-gray-100 text-gray-600 capitalize">
                                        {skill.category}
                                    </span>
                                    {IconComponent && <IconComponent className="w-4 h-4 text-[#1f75cb]" />}
                                </div>
                                <h3 className="font-bold text-gray-900">{skill.name}</h3>
                                {skill.level !== undefined && (
                                    <div className="w-full bg-gray-200 rounded-full h-1.5 mt-3">
                                        <div className="bg-[#1f75cb] h-1.5 rounded-full" style={{ width: `${skill.level}%` }}></div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
